import React, { useEffect, useMemo, useRef } from 'react';
import { useBrowserMonitoring } from 'platform/monitoring/Datadog';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { RequiredLoginView } from '@department-of-veterans-affairs/platform-user/RequiredLoginView';
import { VA_FORM_IDS } from 'platform/forms/constants';
import {
  WIZARD_STATUS_COMPLETE,
  WIZARD_STATUS_RESTARTING,
} from '@department-of-veterans-affairs/platform-site-wide/wizard';
import { isLoggedIn } from 'platform/user/selectors';
import { setData } from 'platform/forms-system/src/js/actions';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import { scrollToTop } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import {
  useFeatureToggle,
  TOGGLE_NAMES,
} from 'platform/utilities/feature-toggles';
import {
  LOAD_STATUSES,
  SAVE_STATUSES,
} from 'platform/forms/save-in-progress/actions';
import { resetScreenerSession } from './subtask/screenerSession';
import formConfig from './config/form';
import AddPerson from '../shared/containers/AddPerson';
import { listMissingIdentifiers } from '../shared/utils/missingIdentifiers';
import {
  serviceRequired,
  idRequired,
  hasRequiredServices,
  hasRequiredId,
  hasRequiredDob,
} from '../shared/utils/entryRequirements';
import useEntryGateTracking from '../shared/hooks/useEntryGateTracking';
import Form526RoutedSavableApp from './containers/Form526RoutedSavableApp';
import ITFWrapper from './containers/ITFWrapper';
import {
  GET_STARTED_PATH,
  START_PATH,
  DOCUMENT_TITLE_SUFFIX,
  PAGE_TITLE_SUFFIX,
  SHOW_8940_4192,
  DATA_DOG_TOGGLE,
  DATA_DOG_ID,
  DATA_DOG_TOKEN,
  DATA_DOG_SERVICE,
  DATA_DOG_VERSION,
  TRACKING_526EZ_SIDENAV_FORM_START,
  TRACKING_526EZ_SIDENAV_FEATURE_TOGGLE,
  MEDIUM_SCREEN_WIDTH,
} from './constants';
import { TRACKING_526EZ_0781_FLOW } from '../shared/constants';
import {
  isBDD,
  getPageTitle,
  isExpired,
  isNewConditionsOn,
  show526Wizard,
  showSubform8940And4192,
  wrapWithBreadcrumb,
  setResumeFormDataForReturnUrlNormalization,
  setResumeLiveToggleValues,
} from './utils';
import { getRestartDestination } from './utils/restartRouting';
import { showForm0781Pages } from '../form0781/utils';
import { getStartPath, getWizardStatus } from './utils/startFlow';
import {
  trackAppEntered,
  trackFormStarted,
} from './utils/tracking/features/allClaimsTracking';
import { track0781FormSubmissionWith0781Data } from '../shared/utils/tracking/form0781Tracking';
import {
  clearBranches,
  fetchBranches,
  getBranches,
} from './utils/serviceBranches';
import { Missing526Identifiers } from '../shared/containers/Missing526Identifiers';
import ClaimFormSideNav from './components/ClaimFormSideNav';
import ClaimFormSideNavErrorBoundary from './components/ClaimFormSideNavErrorBoundary';
import NavButtonsWithTracking from './components/NavButtonsWithTracking';
import useMediaQuery from './hooks/useMediaQuery';
import useConditionsTransformConfig from './hooks/useConditionsTransformConfig';
import { getConditionsTransformCount } from './utils/tracking/features/conditionsWorkflowMigrationTracking';
import {
  trackFormSubmissionFailed,
  trackFormSubmitted,
} from './utils/tracking/features/submissionTracking';

// formConfig must be mutated in place because the platform forms system reads it
// from route objects built at module load time via createRoutesWithSaveInProgress(formConfig).
if (!formConfig.formOptions) {
  formConfig.formOptions = {};
}
formConfig.formOptions.useWebComponentForNavigation = true;
formConfig.formOptions.NavButtonsWithWrapper = NavButtonsWithTracking;

// The platform restart callback receives no arguments, and onFormLoaded only
// receives props connected by RoutedSavableApp. Build the config at this
// boundary so both callbacks use the same live feature-toggle value.
// When the new screener flow reaches 100%, make /introduction the static
// restart destination in formConfig and onFormLoaded, remove this factory and
// its useMemo, and pass formConfig directly to Form526RoutedSavableApp again.
export const createRestartAwareFormConfig = newScreenerQuestionFlow => ({
  ...formConfig,
  saveInProgress: {
    ...formConfig.saveInProgress,
    restartFormCallback: () => {
      resetScreenerSession();
      return getRestartDestination({ newScreenerQuestionFlow });
    },
  },
  onFormLoaded: props =>
    formConfig.onFormLoaded({ ...props, newScreenerQuestionFlow }),
});

const defaultSubmit = formConfig.submit;
formConfig.submit = (submittedForm, formConfigParam, options) => {
  return defaultSubmit(submittedForm, formConfigParam, options).then(
    result => {
      const formData = submittedForm?.data;
      if (showForm0781Pages(formData) && formData?.mentalHealthWorkflowChoice) {
        track0781FormSubmissionWith0781Data({
          workflowChoice: formData.mentalHealthWorkflowChoice,
        });
      }
      // Single entry point for all submit-time tracking.
      trackFormSubmitted(formData);
      return result;
    },
    error => {
      // Mirror success tracking so failure rates can be compared by IPF
      // version and transform state.
      trackFormSubmissionFailed(submittedForm?.data, error);
      return Promise.reject(error);
    },
  );
};

export {
  serviceRequired,
  idRequired,
  hasRequiredServices,
  hasRequiredId,
  hasRequiredDob,
};

export const isIntroPage = ({ pathname = '' } = {}) =>
  pathname.endsWith('/introduction');

export const isConfirmationPage = ({ pathname = '' } = {}) =>
  pathname.replace(/\/+$/, '').endsWith('/confirmation');

export const Form526Entry = ({
  children,
  featureToggles,
  form,
  inProgressFormId,
  isBDDForm,
  itf,
  location,
  loggedIn,
  router,
  savedForms,
  setFormData,
  showSubforms,
  showWizard,
  startPath,
  user,
}) => {
  const { profile = {} } = user;
  const wizardStatus = getWizardStatus();

  const hasSavedForm = savedForms.some(
    savedForm =>
      savedForm.form === formConfig.formId &&
      !isExpired(savedForm.metaData?.expiresAt),
  );

  const hasPrefillData = (profile.prefillsAvailable || []).includes(
    formConfig.formId,
  );

  // Track when user starts the form from introduction page
  // Only tracks once per session when user navigates to first form page without a saved form
  useEffect(() => {
    const isFirstFormPage = location?.pathname === '/veteran-information';
    const hasNoSavedForm = !hasSavedForm;
    const alreadyTracked =
      sessionStorage.getItem(TRACKING_526EZ_SIDENAV_FORM_START) === 'true';

    if (isFirstFormPage && hasNoSavedForm && !alreadyTracked) {
      trackFormStarted(form?.data);
      try {
        sessionStorage.setItem(TRACKING_526EZ_SIDENAV_FORM_START, 'true');
      } catch (error) {
        // Storage access blocked - tracking will still work without this flag
      }
    }
  }, [location?.pathname, hasSavedForm, featureToggles, form?.data]);

  const title = `${getPageTitle(isBDDForm)}${
    isIntroPage(location) ? ` ${PAGE_TITLE_SUFFIX}` : ''
  }`;
  document.title = `${title}${DOCUMENT_TITLE_SUFFIX}`;

  const showLoading = ({ restarting } = {}) => {
    const message = restarting
      ? 'Please wait while we restart the application for you.'
      : 'Please wait while we load the application for you.';
    const label = restarting ? 'restarting' : 'loading';
    return (
      <h1 className="vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--normal">
        <va-loading-indicator message={message} label={label} />
      </h1>
    );
  };

  // start focus on breadcrumb nav when wizard is visible
  const setPageFocus = focusTarget => {
    focusElement(focusTarget);
    scrollToTop();
  };

  useEffect(() => {
    if (wizardStatus === WIZARD_STATUS_COMPLETE && isIntroPage(location)) {
      setPageFocus('h1');
      // save feature flag for 8940/4192
      sessionStorage.setItem(SHOW_8940_4192, showSubforms);
    }
    // Set stable user identity on Datadog RUM. Uses the standard `id`
    // field so Datadog can correlate sessions, replays, and errors by user.
    if (
      typeof window.DD_RUM?.setUserProperty === 'function' &&
      profile.accountUuid
    ) {
      window.DD_RUM.setUserProperty('id', profile.accountUuid);
    }
  }, [inProgressFormId, location, profile, showSubforms, wizardStatus]);

  // Manage inProgressFormId as Datadog RUM global context separately so it
  // is cleared whenever the value becomes falsy (e.g., store reset on
  // logout) and on unmount, preventing stale values from tagging unrelated
  // events.
  useEffect(() => {
    const rum = window.DD_RUM;
    if (inProgressFormId) {
      rum?.setGlobalContextProperty?.('526inProgressFormId', inProgressFormId);
    } else {
      rum?.removeGlobalContextProperty?.('526inProgressFormId');
    }
    return () => {
      rum?.removeGlobalContextProperty?.('526inProgressFormId');
    };
  }, [inProgressFormId]);

  // Expose the session's conditions workflow transform count (incremented by
  // applyConditionsDataTransform whenever an IPF is migrated between the
  // v1/v2 conditions data models) as Datadog RUM global context so every
  // event in the session carries it. Enables monitors on repeated
  // transformations (e.g. @context.526conditionsTransformCount above a
  // threshold) to detect flatten/unflatten loops.
  // Read during render: the transform increments the counter before
  // dispatching the transformed form data, so the re-render that follows
  // always sees the fresh count. Keying the effect on the primitive count
  // (rather than form data identity) means it only re-fires when the count
  // actually changes.
  const conditionsTransformCount = getConditionsTransformCount();
  useEffect(() => {
    const rum = window.DD_RUM;
    if (conditionsTransformCount > 0) {
      rum?.setGlobalContextProperty?.(
        '526conditionsTransformCount',
        conditionsTransformCount,
      );
    }
    return () => {
      rum?.removeGlobalContextProperty?.('526conditionsTransformCount');
    };
  }, [conditionsTransformCount]);

  useEffect(() => {
    if (!loggedIn) {
      clearBranches();
    } else if (!getBranches().length) {
      fetchBranches();
    }
  }, [loggedIn]);

  const hasTrackedAppEnteredRef = useRef(false);
  useEffect(() => {
    if (!loggedIn || !profile.verified || hasTrackedAppEnteredRef.current) {
      return;
    }

    trackAppEntered(form?.data);
    hasTrackedAppEnteredRef.current = true;
  }, [loggedIn, profile.verified, profile.accountUuid, form?.data]);

  const { useFormFeatureToggleSync, useToggleLoadingValue, useToggleValue } =
    useFeatureToggle();
  useFormFeatureToggleSync([
    'disability526Enable2024Form4142',
    'disability526ToxicExposureOptOutDataPurge',
    'disability526SupportingEvidenceEnhancement',
    'disability526SupportingEvidenceFileInputV3',
    'disability526NewBddShaEnforcementWorkflowEnabled',
    'disability526SidenavEnabled',
  ]);

  // including this helper to showLoading when feature toggles are loading
  const togglesLoading = useToggleLoadingValue();

  const supportingEvidenceEnhancement = useToggleValue(
    TOGGLE_NAMES.disability526SupportingEvidenceEnhancement,
  );
  const supportingEvidenceFileInputV3 = useToggleValue(
    TOGGLE_NAMES.disability526SupportingEvidenceFileInputV3,
  );
  // These inform whether to migrate the IPF to the conditions v2 data model
  useConditionsTransformConfig({ togglesLoading, setFormData });

  // New-session resume: SIP returnUrl and saved toggle fields can be stale after
  // logout when flags changed. Feed live Flipper values into resume normalization.
  if (!togglesLoading) {
    setResumeLiveToggleValues({
      disability526SupportingEvidenceEnhancement: supportingEvidenceEnhancement,
      disability526SupportingEvidenceFileInputV3: supportingEvidenceFileInputV3,
    });
    setResumeFormDataForReturnUrlNormalization(form?.data);
  }

  // Sidenav related feature flags
  // We don't really need this feature toggle in formData since it's only used here
  const sideNavFeatureEnabled = useToggleValue(
    TOGGLE_NAMES.disability526SidenavEnabled,
  );

  // When enabled, this suppresses the /get-started auto-redirect for all
  // authenticated veterans. They remain on the authenticated introduction
  // page instead of being sent to the screener flow.
  const newScreenerQuestionFlow = useToggleValue(
    TOGGLE_NAMES.disability526NewScreenerQuestionFlow,
  );

  const restartAwareFormConfig = useMemo(
    () => createRestartAwareFormConfig(newScreenerQuestionFlow),
    [newScreenerQuestionFlow],
  );

  // Detect screen size for conditional rendering of sidenav component
  const isWideScreen = useMediaQuery(`(min-width: ${MEDIUM_SCREEN_WIDTH}px)`);

  // Persist the sidenav toggle to sessionStorage so tracking functions
  // (which run outside React) can read it without prop drilling.
  useEffect(() => {
    if (sideNavFeatureEnabled !== undefined) {
      sessionStorage.setItem(
        TRACKING_526EZ_SIDENAV_FEATURE_TOGGLE,
        String(sideNavFeatureEnabled),
      );
    }
  }, [sideNavFeatureEnabled]);

  useEntryGateTracking({
    loggedIn,
    profile,
    formId: VA_FORM_IDS.FORM_21_526EZ,
    getIsBdd: () => isBDD(form?.data),
  });

  // Persist modern 0781 flow choice so tracking can correlate with backend StatsD.
  useEffect(() => {
    const syncModern0781Flow = form?.data?.syncModern0781Flow;
    try {
      if (syncModern0781Flow !== undefined) {
        sessionStorage.setItem(
          TRACKING_526EZ_0781_FLOW,
          String(syncModern0781Flow),
        );
      } else {
        sessionStorage.removeItem(TRACKING_526EZ_0781_FLOW);
      }
    } catch (error) {
      // Storage access blocked - get0781TrackingDefaults() will treat as unset
    }
  }, [form?.data?.syncModern0781Flow]);

  // Set disability/526 feature toggles as Datadog RUM global context so RUM
  // events can be filtered by feature cohort.
  useEffect(() => {
    const rum = window.DD_RUM;
    const cleanup = () => {
      try {
        rum?.removeGlobalContextProperty?.('526FeatureFlags');
      } catch (error) {
        // RUM context updates should never break the form experience.
      }
    };

    if (togglesLoading || !featureToggles) {
      return cleanup;
    }

    try {
      const toCamelCase = key =>
        key.replace(/_([a-z0-9])/g, (_match, group) => group.toUpperCase());

      const trackedToggleEntries = Object.entries(featureToggles)
        .filter(([key]) => key.startsWith('disability') || key.includes('526'))
        .map(([key, value]) => [toCamelCase(key), value === true])
        .sort(([a], [b]) => a.localeCompare(b));

      const featureFlags = Object.fromEntries(trackedToggleEntries);

      if (trackedToggleEntries.length > 0) {
        rum?.setGlobalContextProperty?.('526FeatureFlags', featureFlags);
      } else {
        cleanup();
      }
    } catch (error) {
      cleanup();
    }

    return cleanup;
  }, [togglesLoading, featureToggles]);

  useBrowserMonitoring({
    loggedIn: true,
    toggleName: DATA_DOG_TOGGLE,
    applicationId: DATA_DOG_ID,
    clientToken: DATA_DOG_TOKEN,
    service: DATA_DOG_SERVICE,
    version: DATA_DOG_VERSION,
    // Current recommendation is to record 100% and filter in DD retention filters, since swap to unlimited plan?
    // Will confirm
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask',
    beforeSend: event => {
      // Prevent PII from being sent to Datadog with click actions.
      if (event.action?.type === 'click') {
        // eslint-disable-next-line no-param-reassign
        event.action.target.name = 'Clicked item';
      }
      return true;
    },
    // sessionReplaySampleRate: environment.vspEnvironment() === 'staging' ? 100 : 10,
  });

  const shouldRedirectToIntroduction = !loggedIn && !isIntroPage(location);
  const shouldRedirectToStartPath =
    loggedIn &&
    showWizard &&
    !togglesLoading &&
    !newScreenerQuestionFlow &&
    !isConfirmationPage(location) &&
    ((!hasSavedForm && wizardStatus !== WIZARD_STATUS_COMPLETE) ||
      (hasSavedForm && wizardStatus === WIZARD_STATUS_RESTARTING));

  useEffect(() => {
    if (shouldRedirectToIntroduction) {
      router.push('/introduction');
    } else if (shouldRedirectToStartPath) {
      router.push(startPath);
    }
  }, [
    router,
    shouldRedirectToIntroduction,
    shouldRedirectToStartPath,
    startPath,
  ]);

  // The router should be doing this, but we're getting lots of Sentry errors
  // See va.ghe.com/software/va.gov-team/issues/29893
  if (shouldRedirectToIntroduction) {
    return wrapWithBreadcrumb(title, showLoading());
  }

  // showWizard feature flag loads _after_ page has rendered causing the full
  // page content to render, then the wizard to render if this flag is true, so
  // we show a loading indicator until the feature flags are available. This
  // can be removed once the feature flag is removed
  // Including togglesLoading which I think is the intended functionality here we can keep even
  //  after the showWizard is deprecated since it's probably best practice
  if (typeof showWizard === 'undefined' || togglesLoading) {
    return wrapWithBreadcrumb(title, showLoading());
  }

  // showWizard feature flag is initially undefined
  //
  // The confirmation page is a terminal state and must never re-route to
  // /get-started. After a successful submit, an async Redux change (profile refetch
  // removing the in-progress form, so `hasSavedForm` flips false; and the
  // ConfirmationPoll status poll) re-renders this component while the user is
  // still on /confirmation. Combined with ConfirmationPage.componentDidMount() clearing
  // WIZARD_STATUS, that would satisfy the redirect condition below and bounce
  // the user to /get-started mid-print. Excluding /confirmation prevents that.
  //
  // When disability_526_new_screener_question_flow is enabled, this suppresses
  // the entire authenticated auto-redirect to /get-started (for both saved-form
  // and no-saved-form paths). /get-started remains reachable from the authenticated
  // introduction page via the "go back and answer questions again" link.
  if (shouldRedirectToStartPath) {
    return wrapWithBreadcrumb(title, showLoading({ restarting: true }));
  }

  // wraps the app and redirects user if they are not enrolled
  const content = (
    <Form526RoutedSavableApp
      formConfig={restartAwareFormConfig}
      currentLocation={location}
      skipPrefill={!hasPrefillData}
    >
      {children}
    </Form526RoutedSavableApp>
  );

  // Not logged in, so show the rendered content. The RoutedSavableApp shows
  // an alert with the sign in button
  if (!loggedIn) {
    return wrapWithBreadcrumb(title, content);
  }

  // "add-person" service means the user has a edipi and SSN in the system, but
  // is missing either a BIRLS or participant ID.
  if (profile.services.includes('add-person')) {
    return wrapWithBreadcrumb(title, <AddPerson title={title} />);
  }

  // RequiredLoginView will handle unverified users by showing the
  // appropriate link
  if (profile.verified && listMissingIdentifiers(profile)) {
    const identifiers = profile.claims.form526RequiredIdentifierPresence;

    return wrapWithBreadcrumb(
      title,
      <Missing526Identifiers
        title={title}
        form526RequiredIdentifiers={identifiers}
      />,
    );
  }

  // SideNav MVP functionality
  // Always render the grid layout when sidenav feature is enabled to keep
  // Form526RoutedSavableApp at a stable tree depth. This prevents React from
  // remounting the class component when form data transitions between v1 and
  // v2 shapes (which would trigger an infinite IPF re-fetch loop).
  // The ClaimFormSideNav itself is only rendered when newConditionsWorkflow is active.
  if (sideNavFeatureEnabled) {
    const newConditionsOn = isNewConditionsOn(form?.data);
    const hideNavPaths = [
      '/confirmation',
      '/form-saved',
      '/introduction',
      START_PATH,
      GET_STARTED_PATH,
    ];

    const pathname = location?.pathname?.replace(/\/+$/, '') || '';
    const loadedStatus = form?.loadedStatus;
    const savedStatus = form?.savedStatus;
    const isFormDataLoaded =
      loadedStatus === LOAD_STATUSES.success ||
      loadedStatus === LOAD_STATUSES.notAttempted;
    const isFormSaving = savedStatus === SAVE_STATUSES.pending;

    const shouldHideNav =
      !newConditionsOn ||
      hideNavPaths.some(p => pathname.endsWith(p)) ||
      !itf?.messageDismissed ||
      !isFormDataLoaded ||
      isFormSaving;
    const contentHiddenSideNavClass = shouldHideNav
      ? ``
      : ` medium-screen:vads-grid-col-9`;

    // Create side nav component (conditionally rendered based on screen size)
    const sideNavComponent = newConditionsOn ? (
      <ClaimFormSideNavErrorBoundary pathname={pathname} formData={form?.data}>
        <ClaimFormSideNav
          enableAnalytics
          formData={form?.data}
          pathname={pathname}
          router={router}
          setFormData={setFormData}
        />
      </ClaimFormSideNavErrorBoundary>
    ) : null;

    // Mobile: Wrap children with mobile side nav to inject it after Progress Bar
    // Only render on mobile screens (!isWideScreen)
    const wrappedChildren = (
      <>
        {!isWideScreen && !shouldHideNav && sideNavComponent && (
          <div className="vads-u-margin-bottom--2">{sideNavComponent}</div>
        )}
        {children}
      </>
    );

    // Rebuild content with wrapped children for mobile side nav positioning
    const contentWithMobileSideNav = (
      <Form526RoutedSavableApp
        formConfig={restartAwareFormConfig}
        currentLocation={location}
        skipPrefill={!hasPrefillData}
      >
        {wrappedChildren}
      </Form526RoutedSavableApp>
    );

    return wrapWithBreadcrumb(
      title,
      <article
        className="vads-grid-container"
        id="form-526"
        data-location={`${location?.pathname?.slice(1)}`}
      >
        <div className="vads-grid-row vads-u-margin-x--neg2p5">
          {/* Desktop: Render side nav in left sidebar column */}
          {isWideScreen && !shouldHideNav && sideNavComponent && (
            <div className="vads-u-padding-x--2p5 vads-u-padding-bottom--3 vads-grid-col-12 medium-screen:vads-grid-col-3">
              {sideNavComponent}
            </div>
          )}
          <div
            className={`vads-u-padding-x--2p5 vads-grid-col-12${contentHiddenSideNavClass}`}
          >
            <RequiredLoginView
              serviceRequired={serviceRequired}
              user={user}
              verify
            >
              <ITFWrapper location={location} title={title}>
                {contentWithMobileSideNav}
              </ITFWrapper>
            </RequiredLoginView>
          </div>
        </div>
      </article>,
    );
  }

  return wrapWithBreadcrumb(
    title,
    <article id="form-526" data-location={`${location?.pathname?.slice(1)}`}>
      <RequiredLoginView serviceRequired={serviceRequired} user={user} verify>
        <ITFWrapper location={location} title={title}>
          {content}
        </ITFWrapper>
      </RequiredLoginView>
    </article>,
  );
};

Form526Entry.propTypes = {
  accountUuid: PropTypes.string,
  children: PropTypes.any,
  featureToggles: PropTypes.object,
  form: PropTypes.shape({
    data: PropTypes.object,
    loadedStatus: PropTypes.string,
    savedStatus: PropTypes.string,
  }),
  inProgressFormId: PropTypes.number,
  isBDDForm: PropTypes.bool,
  isStartingOver: PropTypes.bool,
  itf: PropTypes.shape({
    messageDismissed: PropTypes.bool,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  loggedIn: PropTypes.bool,
  mvi: PropTypes.shape({
    addPersonState: PropTypes.string,
  }),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  savedForms: PropTypes.array,
  setFormData: PropTypes.func,
  showSubforms: PropTypes.bool,
  showWizard: PropTypes.bool,
  startPath: PropTypes.string,
  user: PropTypes.shape({
    profile: PropTypes.shape({}),
  }),
};

const mapStateToProps = state => ({
  accountUuid: state?.user?.profile?.accountUuid,
  featureToggles: toggleValues(state),
  form: state?.form,
  inProgressFormId: state?.form?.loadedData?.metadata?.inProgressFormId,
  isBDDForm: isBDD(state?.form?.data),
  isStartingOver: state.form?.isStartingOver,
  itf: state.itf,
  loggedIn: isLoggedIn(state),
  mvi: state.mvi,
  savedForms: state?.user?.profile?.savedForms || [],
  showSubforms: showSubform8940And4192(state),
  showWizard: show526Wizard(state),
  startPath: getStartPath(state),
  user: state.user,
});

const mapDispatchToProps = dispatch => ({
  setFormData: data => dispatch(setData(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form526Entry);
